import type { WalletInstitution } from '@/modules/Wallet/interface/walet';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  AccountBalanceOutline,
  InfoOutline,
} from '@eqi-investimentos/icons/web';
import { Flex, Text, Tooltip } from '@eqi-investimentos/react';
import {
  INVESTMENT_ORDER_TYPES_TRANSLATE,
  type InvestmentOrder,
} from '@/modules/Wallet/interface/invest';
import { useT } from '@/libs/i18n/utils/useT';
import { salesforceOpportunityConfig } from '@/config/salesForce';
import { CardMyAdvisor } from '@/components/modules/CardMyAdvisor';
import { MonetaryVisibilityButton } from '@/components/modules/MonetaryVisibilityButton';
import { useCreateSFOpportunity } from '@/modules/Customer/http/client/mutations/useCreateSFOpportunity';
import { useGetCustomerAdvisor } from '@/modules/Customer/http/client/useGetCustomerAdvisor';
import { useGetCustomerStatus } from '@/modules/Customer/http/client/useGetCustomerStatus';
import { useGetWalletInstitutions } from '@/modules/Wallet/http/client/useGetWalletInstitutions';
import { useInvest } from '@/contexts/InvestContext';
import { useCheckWhatsappHandsUp } from '@/hooks/useCheckWhatsappHandsUp';
import { Icon } from '@/components/elements/Icon';
import { Thumb } from '@/components/elements/Thumb';
import { formatNumberToPTBRCurrency } from '@/utils/format';
import { IfRender } from '@/utils/jsx';
import { Drawer } from '../Drawer';
import { AppsAndBrokersSection } from './AppsAndBrokersSection';
import { BrokersSection } from './BrokersSection';
import { ConfirmSendOrderAdvisor } from './ConfirmSendOrderAdvisor';
import { SelectAssetSection } from './SelectAssetSection';
import { SendOrderAdvisorSection } from './SendOrderAdvisorSection';
import { InvestViewSkeleton } from './Skeleton';

export const InvestModal: FC<InvestModalProps> = ({ id = MODAL_INVEST_ID }) => {
  const investmentModeViewRef = useRef(investStep[0]);
  const t = useT();
  const { selectAsset } = useInvest();
  const { checkToCallWhatsapp } = useCheckWhatsappHandsUp();

  const { data: customerStatus } = useGetCustomerStatus();
  const { data: advisor, isLoading } = useGetCustomerAdvisor();
  const {
    data: { data: institutions } = {
      data: [],
      total: 0,
      hasOF: false,
      hasEqi: false,
    },
  } = useGetWalletInstitutions();
  const { mutate: createSFOpportunity } = useCreateSFOpportunity();

  const [investmentModeView, setInvestmentModeView] = useState(investStep[0]);
  const [investmentOrder, setInvestmentOrder] = useState<InvestmentOrder>();

  const isEqiDigital = !!customerStatus?.isEqiDigital;
  const hasAdvisor = !!customerStatus?.isEqi && !isEqiDigital;

  const handleBackModeInvestView = () => {
    if (investmentModeViewRef.current === investStep[0]) {
      window.dispatchEvent(new Event(`close-chakra-modal-${MODAL_INVEST_ID}`));
      return;
    }
    setInvestmentModeView(investStep[0]);
    investmentModeViewRef.current = investStep[0];
  };

  const setInvestStep = async (mode: string) => {
    setInvestmentModeView(mode);
    investmentModeViewRef.current = mode;
  };

  const setOrderToSend = async (data: InvestmentOrder) => {
    setInvestmentOrder(data);
    setInvestmentModeView(investStep[3]);
    investmentModeViewRef.current = investStep[3];
  };

  const sendOrder = async () => {
    if (!investmentOrder) return;

    const type =
      INVESTMENT_ORDER_TYPES_TRANSLATE[investmentOrder.type].toUpperCase();
    const amount = formatNumberToPTBRCurrency(Number(investmentOrder?.amount));
    const assetTitle = getAssetTitle(investmentOrder);
    const ticker =
      'ticker' in investmentOrder.asset ? investmentOrder.asset.ticker : '';

    if (hasAdvisor) {
      createSFOpportunity({
        action: salesforceOpportunityConfig.banking.invest.action,
        product: salesforceOpportunityConfig.banking.invest.product,
        amount,
        rawQuestions: { amount, ticker },
        translate: () => type,
      });
    }

    const messageWpp = `\n\n Olá, ${advisor?.advisorName}, gostaria de ${type?.toUpperCase()} a quantia de ${amount} no ativo ${assetTitle} (${ticker}), pode me ajudar a executar?`;

    checkToCallWhatsapp('default', messageWpp);

    setInvestmentModeView(investStep[0]);
    investmentModeViewRef.current = investStep[0];
  };

  const walletBalances: WalletBalances = useMemo(
    () => calculateBalances(institutions, t),
    [institutions, t]
  );

  useEffect(() => {
    window.addEventListener(
      `action-back-button-drawer-${MODAL_INVEST_ID}`,
      handleBackModeInvestView
    );

    return () => {
      window.removeEventListener(
        `action-back-button-drawer-${MODAL_INVEST_ID}`,
        handleBackModeInvestView
      );
      selectAsset({ asset: null, scrollUp: true });
      setInvestmentOrder(undefined);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.dispatchEvent(new Event('recheck-monetary-visibility'));
  }, [investmentModeView, investmentOrder, isLoading, walletBalances.balances]);

  return (
    <Drawer
      id={id}
      customBackButtonAction={() =>
        window.dispatchEvent(new Event(`close-chakra-modal-${id}`))
      }
      showIconButton={false}
      bodyProps={{ p: 0 }}
      closeButtonProps={{ top: '3.25rem' }}
      headerProps={{
        pt: '48px',
        pl: '4.0625rem',
        pr: '3rem',
        py: '2.1875rem',
      }}
      headerContent={
        <Text size="xl" color="content.primary">
          Investir
        </Text>
      }
    >
      <Flex id={CONTAINER_WIDGET_ID} flexDir="column" gap="2xl" pb="4xl">
        <IfRender condition={isLoading}>
          <InvestViewSkeleton />
        </IfRender>
        <IfRender condition={!isLoading}>
          <IfRender condition={investmentModeView === investStep[0]}>
            <Flex flexDir="column" gap="2xl" px="2xl" py="l" pt="30">
              <Flex justifyContent="space-between" alignItems="center" w="full">
                <Flex flexDir="column" gap="2xs" w="full">
                  <Flex gap="xs" w="full" align="center">
                    <Text size="l" fontWeight="light">
                      {t('screens.invest.amountTitle')}
                    </Text>
                    <Tooltip
                      label={t('screens.invest.tooltipBalance')}
                      fontSize="xs"
                      px="xs"
                      color="content.inverse"
                      bgColor="content.secondary"
                      borderRadius="2xs"
                    >
                      <Flex justifyContent="center">
                        <InfoOutline
                          w={16}
                          h={16}
                          color="content.brand"
                          cursor="pointer"
                        />
                      </Flex>
                    </Tooltip>
                  </Flex>
                  <Text
                    className="hide-monetary-left"
                    size="h5"
                    color="content.primary"
                  >
                    {formatNumberToPTBRCurrency(walletBalances.total)}
                  </Text>
                </Flex>
                <MonetaryVisibilityButton
                  widgetId={CONTAINER_WIDGET_ID}
                  display={{ base: 'none', md: 'flex' }}
                />
              </Flex>
              <Flex gap="m" w="full">
                {walletBalances.balances.map(({ label, value }) => (
                  <Flex key={label} flexDir="column" gap="3xs" minW="5.5rem">
                    <Flex w="full" align="center" gap="xs">
                      <IfRender condition={label.toLowerCase() === 'eqi'}>
                        <Thumb
                          src="/assets/eqi-logo.svg"
                          width={24}
                          height={24}
                          radius={8}
                          fit="cover"
                          alt="Logotipo 'EQI' em branco sobre fundo verde-escuro."
                        />
                      </IfRender>
                      <IfRender condition={label.toLowerCase() !== 'eqi'}>
                        <AccountBalanceOutline
                          width={24}
                          height={24}
                          color="content.brand"
                        />
                      </IfRender>
                      <Text size="l" color="content.primary">
                        {label}
                      </Text>
                    </Flex>
                    <Text size="l" color="content.secondary">
                      {formatNumberToPTBRCurrency(value)}
                    </Text>
                  </Flex>
                ))}
              </Flex>
              <IfRender condition={walletBalances.total === 0}>
                <Flex
                  w="full"
                  bgColor="background.brandLight"
                  borderRadius="lg"
                  px="m"
                  py="s"
                  gap="l"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon
                    iconName="AlertCircleFilled"
                    width={24}
                    height={24}
                    color="content.primary"
                  />
                  <Flex flexDir="column" h="full" gap="3xs">
                    <Text fontWeight="medium" color="content.primary" size="xl">
                      {t('screens.invest.noBalance.title')}
                    </Text>
                    <Text size="l" color="content.primary">
                      {t('screens.invest.noBalance.description')}
                    </Text>
                  </Flex>
                </Flex>
              </IfRender>
            </Flex>
            <IfRender condition={walletBalances.total > 0}>
              <SendOrderAdvisorSection
                handleSelectAsset={() => setInvestStep(investStep[1])}
                handleSendOrder={setOrderToSend}
              />
            </IfRender>
            <AppsAndBrokersSection balance={walletBalances.total} />
            <IfRender condition={!!advisor?.advisorName}>
              <Flex
                justifyContent="center"
                alignItems="center"
                w="full"
                px="2xl"
              >
                <CardMyAdvisor hasAdvisor={!isEqiDigital} variant="mobile" />
              </Flex>
            </IfRender>
          </IfRender>
          <Flex px="2xl" w="full">
            <IfRender condition={investmentModeView === investStep[1]}>
              <SelectAssetSection />
            </IfRender>
            <IfRender condition={investmentModeView === investStep[2]}>
              <BrokersSection />
            </IfRender>
          </Flex>
          {investmentOrder && investmentModeView === investStep[3] && (
            <ConfirmSendOrderAdvisor
              orderDataToSend={investmentOrder}
              handleBack={() => setInvestStep(investStep[0])}
              handleConfirmAndSend={sendOrder}
            />
          )}
        </IfRender>
      </Flex>
    </Drawer>
  );
};

const CONTAINER_WIDGET_ID = 'invest-drawer-widget';
export const MODAL_INVEST_ID = 'invest-modal';
export const MODAL_CONNECTORS_ID = 'connectors-modal';
export const investStep = [
  'invest_initial',
  'select_asset',
  'redirect_broker',
  'send_order_advisor',
];

const calculateBalances = (
  institutions: WalletInstitution[],
  t: (key: string) => string
): WalletBalances => {
  if (!institutions || institutions.length === 0) {
    return { total: 0, balances: [] };
  }

  const total = institutions.reduce(
    (acc, { checking_account_total }) => acc + checking_account_total,
    0
  );

  const totalEqi =
    institutions.find(({ name }) => name.toLowerCase() === 'eqi')
      ?.checking_account_total ?? 0;

  return {
    total,
    balances: [
      { label: t('screens.invest.balance.eqi'), value: totalEqi },
      { label: t('screens.invest.balance.others'), value: total - totalEqi },
    ],
  };
};

const getAssetTitle = ({ asset }: InvestmentOrder) => {
  if ('title' in asset) {
    return asset.title;
  }
  return asset.name;
};

type InvestModalProps = {
  id?: string;
};

type WalletBalances = {
  balances: { label: string; value: number }[];
  total: number;
};
